import React, { useState } from 'react';
import Layout from 'layout/Layout';
import { useNavigate } from 'react-router-dom';
import { useCreateWorkshopMutation } from 'api/workshopApi';
import { useGetAllMembersQuery } from 'api/consultancyApi';
import Swal from 'sweetalert2';

const CreateWorkshop = () => {
  const [formState, setFormState] = useState({
    title: '',
    subtitle: '',
    date: '',
    start_time: '',
    end_time: '',
    duration: '',
    course_fee_original: '',
    course_fee_discounted: '',
    location: '',
    languages: '',
    cover_photo: null,
    host_slug: '',
  });

  const [errors, setErrors] = useState({});

  const { data: membersData, isLoading: membersLoading, isError: membersError } = useGetAllMembersQuery();
  const [createWorkshop, { isLoading: isCreating }] = useCreateWorkshopMutation();
  const members = membersData?.data || [];
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['title', 'subtitle', 'date', 'duration', 'start_time', 'end_time', 'course_fee_original', 'course_fee_discounted', 'location', 'host_slug'];
    
    requiredFields.forEach(field => {
      if (!formState[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    if (!formState.cover_photo) {
      newErrors.cover_photo = 'Cover photo is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields.',
        confirmButtonColor: '#d33',
      });
    }

    try {
      const formData = new FormData();

      Object.entries(formState).forEach(([key, val]) => {
        if (key === 'languages' && val) {
          const languagesArray = val.split(',').map((lang) => lang.trim()).filter(Boolean);
          formData.append(key, JSON.stringify(languagesArray));
        } else if (val !== null && val !== '') {
          formData.append(key, val);
        }
      });

      await createWorkshop(formData).unwrap();

      Swal.fire({
        title: 'Success!',
        text: 'Workshop created successfully.',
        icon: 'success',
        confirmButtonColor: '#10b981',
      }).then(() => {
        navigate('/workshop');
      });

    } catch (err) {
      console.error('Create error:', err);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create workshop.',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    }
  };

  const InputField = ({ label, name, type = 'text', placeholder }) => (
    <div className="flex flex-col">
      <label className="mb-1 text-sm font-semibold text-gray-700">{label} <span className="text-red-500">*</span></label>
      <input
        type={type}
        name={name}
        value={formState[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition-all ${
          errors[name] ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
        }`}
      />
      {errors[name] && <span className="text-red-500 text-xs mt-1">{errors[name]}</span>}
    </div>
  );

  return (
    <Layout title="Manage Workshops" tagline="Create your workshops here.">
      <div className="max-w-5xl mx-auto mt-10 md:mt-16 p-6 bg-white rounded-xl shadow-lg border border-gray-100 mb-20">
        <div className="border-b pb-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Create New Workshop</h2>
          <p className="text-gray-500 text-sm mt-1">Fill in the details below to publish a new workshop.</p>
        </div>

        <form onSubmit={handleCreate} className="space-y-8">
          
          {/* Basic Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Workshop Title" name="title" placeholder="e.g. Masterclass on Web Dev" />
            <InputField label="Short Subtitle" name="subtitle" placeholder="e.g. Learn React & Node" />
          </div>

          {/* Schedule */}
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="Date" name="date" type="date" />
            <InputField label="Start Time" name="start_time" type="time" />
            <InputField label="End Time" name="end_time" type="time" />
            <div className="md:col-span-3">
              <InputField label="Duration" name="duration" placeholder="e.g. 2 Hours" />
            </div>
          </div>

          {/* Fees & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Original Fee (₹)" name="course_fee_original" type="number" placeholder="0" />
            <InputField label="Discounted Fee (₹)" name="course_fee_discounted" type="number" placeholder="0" />
            <InputField label="Location" name="location" placeholder="e.g. Online (Zoom)" />
            
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-semibold text-gray-700">Languages (Optional)</label>
              <input
                type="text"
                name="languages"
                value={formState.languages}
                onChange={handleInputChange}
                placeholder="e.g. English, Hindi"
                className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Host & Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-semibold text-gray-700">Select Host <span className="text-red-500">*</span></label>
              {membersLoading ? (
                <p className="text-sm text-gray-500 py-2">Loading hosts...</p>
              ) : membersError ? (
                <p className="text-sm text-red-500 py-2">Failed to load hosts.</p>
              ) : (
                <select
                  name="host_slug"
                  value={formState.host_slug}
                  onChange={handleInputChange}
                  className={`border rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 transition-all ${
                    errors.host_slug ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
                  }`}
                >
                  <option value="">Choose a Host</option>
                  {members.map((m) => m.slug && (
                    <option key={m.id} value={m.slug}>{m.name}</option>
                  ))}
                </select>
              )}
              {errors.host_slug && <span className="text-red-500 text-xs mt-1">{errors.host_slug}</span>}
            </div>

            <div className="flex flex-col">
              <label className="mb-1 text-sm font-semibold text-gray-700">Cover Photo <span className="text-red-500">*</span></label>
              <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors ${
                errors.cover_photo ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
              }`}>
                <input type="file" name="cover_photo" id="cover_photo" accept="image/*" onChange={handleInputChange} className="hidden" />
                <label htmlFor="cover_photo" className="cursor-pointer flex flex-col items-center w-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-10 w-10 mb-2 ${errors.cover_photo ? 'text-red-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12v9m0-9L9 15m3-3l3 3M12 3v9" />
                  </svg>
                  <span className="text-sm font-medium text-gray-600">
                    {formState.cover_photo ? formState.cover_photo.name : 'Click to upload image'}
                  </span>
                </label>
              </div>
              {errors.cover_photo && <span className="text-red-500 text-xs mt-1 text-center">{errors.cover_photo}</span>}
            </div>
          </div>

          <div className="pt-6 border-t flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/workshop')}
              className="px-6 py-2.5 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-8 py-2.5 rounded-lg font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 shadow-md transition-all"
            >
              {isCreating ? 'Creating...' : 'Publish Workshop'}
            </button>
          </div>

        </form>
      </div>
    </Layout>
  );
};

export default CreateWorkshop;
